import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode'; // Use Html5Qrcode for both
import { FaCamera, FaCheckCircle, FaTimes, FaUpload } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

const QRScanner = () => {
    const scannerRef = useRef(null);
    const [scanResult, setScanResult] = useState(null);
    const [isScanning, setIsScanning] = useState(true);
    const [error, setError] = useState(null);
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const html5QrCodeRef = useRef(null);

    useEffect(() => {
        if (isScanning && scannerRef.current && !html5QrCodeRef.current) {
            html5QrCodeRef.current = new Html5Qrcode("qr-reader");
            html5QrCodeRef.current.start(
                { facingMode: "environment" }, // Use back camera
                {
                    fps: 30, // Higher FPS for faster detection
                    qrbox: { width: 350, height: 350 }, // Larger box for better coverage
                    // supportedFormats removed to avoid undefined error
                },
                (decodedText) => {
                    console.log('QR Code scanned from camera:', decodedText); // Log QR data to console
                    setScanResult(decodedText);
                    setError(null); // Clear any errors on success
                },
                (errorMessage) => {
                    // Only set error for real issues, not "no QR found"
                    if (!errorMessage.includes('NotFoundException')) {
                        setError(errorMessage.toString());
                    } else {
                        setError('Position QR code in frame for scanning.'); // Friendly message
                    }
                }
            ).catch((err) => {
                console.error('Error starting camera:', err);
                setError('Failed to start camera. Please check permissions.');
            });
        } else if (!isScanning && html5QrCodeRef.current && html5QrCodeRef.current.getState() === Html5Qrcode.SCAN_STATE_STARTED) {
            html5QrCodeRef.current.stop().catch(console.error);
        }

        return () => {
            if (html5QrCodeRef.current && html5QrCodeRef.current.getState() === Html5Qrcode.SCAN_STATE_STARTED) {
                html5QrCodeRef.current.stop().catch(console.error);
            }
        };
    }, [isScanning]);

    const handleFileScan = async (file) => {
        if (!file || !file.type.startsWith('image/')) {
            setError('Please select a valid image file.');
            return;
        }
        if (html5QrCodeRef.current) {
            const wasScanning = isScanning;
            try {
                // Stop camera if running
                if (html5QrCodeRef.current.getState() === Html5Qrcode.SCAN_STATE_STARTED) {
                    await html5QrCodeRef.current.stop();
                }
                const decodedText = await html5QrCodeRef.current.scanFile(file, false);
                console.log('QR Code scanned from file:', decodedText); // Log QR data to console
                setScanResult(decodedText);
                setError(null);
            } catch (err) {
                console.error('Error scanning file:', err);
                if (err && err.message && err.message.includes('NotFoundException')) {
                    setError('No QR code detected in the uploaded image. Ensure the image is clear and the QR code is prominent.');
                } else {
                    setError('Error scanning file: ' + (err.message || 'Unknown error'));
                }
            } finally {
                // Only restart camera if it was scanning before
                if (wasScanning && html5QrCodeRef.current.getState() !== Html5Qrcode.SCAN_STATE_STARTED) {
                    html5QrCodeRef.current.start(
                        { facingMode: "environment" },
                        { fps: 30, qrbox: { width: 350, height: 350 } }, // supportedFormats removed
                        (decodedText) => {
                            console.log('QR Code scanned from camera:', decodedText);
                            setScanResult(decodedText);
                            setError(null);
                        },
                        (errorMessage) => {
                            if (!errorMessage.includes('NotFoundException')) {
                                setError(errorMessage.toString());
                            } else {
                                setError('Position QR code in frame for scanning.');
                            }
                        }
                    ).catch(console.error);
                }
            }
        }
    };

    const toggleScanning = () => {
        setIsScanning(!isScanning);
        setScanResult(null);
        setError(null);
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            handleFileScan(file);
        }
    };

    const handleRescan = () => {
        setScanResult(null);
        setError(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleClose = () => {
        navigate(-1); // Go back to previous page
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-100 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
                <div className="flex justify-between items-center mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center justify-center gap-2">
                        <FaCamera className="text-blue-500" />
                        QR Scanner
                    </h1>
                    <button
                        onClick={handleClose}
                        className="text-gray-500 hover:text-red-500 transition-colors"
                        title="Close"
                    >
                        <FaTimes size={24} />
                    </button>
                </div>

                {/* Upload Button */}
                <div className="mb-4">
                    <input
                        type="file"
                        ref={fileInputRef}
                        accept="image/*"
                        onChange={handleFileChange}
                        className="hidden"
                        id="qr-upload"
                    />
                    <label
                        htmlFor="qr-upload"
                        className="flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-full cursor-pointer transition-colors"
                    >
                        <FaUpload />
                        Upload QR Image
                    </label>
                </div>

                {/* Toggle Scanning Button */}
                <div className="mb-4">
                    <button
                        onClick={toggleScanning}
                        className={`px-4 py-2 rounded-full transition-colors ${isScanning ? 'bg-red-500 hover:bg-red-600 text-white' : 'bg-green-500 hover:bg-green-600 text-white'}`}
                    >
                        {isScanning ? 'Stop Scanning' : 'Start Scanning'}
                    </button>
                </div>

                {isScanning && (
                    <div className="mb-6">
                        <div id="qr-reader" ref={scannerRef} className="w-full h-64 bg-gray-100 rounded-lg overflow-hidden border-2 border-blue-500"></div> {/* Added border for visual feedback */}
                        <p className="text-sm text-gray-600 mt-4">Position the QR code within the highlighted frame to scan.</p>
                    </div>
                )}

                {scanResult && (
                    <div className="mb-6">
                        <div className="flex items-center justify-center gap-2 mb-4">
                            <FaCheckCircle className="text-green-500" size={24} />
                            <span className="text-lg font-semibold text-green-600">Scan Successful!</span>
                        </div>
                        <p className="text-sm text-gray-700 bg-gray-50 p-4 rounded-lg break-all">
                            <strong>Scanned Data:</strong> {scanResult}
                        </p>
                        <button
                            onClick={handleRescan}
                            className="mt-4 bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-full transition-colors"
                        >
                            Scan Again
                        </button>
                    </div>
                )}

                {error && (
                    <div className="mb-6">
                        <p className="text-sm text-red-600 bg-red-50 p-4 rounded-lg">{error}</p>
                        <button
                            onClick={handleRescan}
                            className="mt-4 bg-red-500 hover:bg-red-600 text-white px-6 py-2 rounded-full transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {!isScanning && !scanResult && !error && (
                    <div className="mb-6">
                        <p className="text-sm text-gray-600">Initializing camera...</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QRScanner;
